/** Central blog configuration — edit order, add posts here. */

export const blogPosts = [
  {
    id: "how-to-organize-tax-documents",
    slug: "how-to-organize-tax-documents",
    title: "How to Organize Tax Documents",
    description: "Organize by tax year first, then by what each document proves on the return. Keep one separate permanent folder for anything that establishes cost basis. File each document the day it arrives rather than at filing time...",
    order: 33,
    image: "/assets/how-to-organize-tax-documents.png",
    imageAlt: "How to Organize Tax Documents",
    contentPath: "/content/how-to-organize-tax-documents.html",
    category: "Tax",
    featured: false,
    layout: "default",
    seoTitle: "How to Organize Tax Documents | AmitaSoft",
    seoDescription: "Most filing systems collapse in year two because filing takes a decision. A folder structure and naming rule that survive, plus what to do if you are behind.",
    publishedDate: "2026-09-01T22:50:00-07:00",
    updatedDate: "2026-09-01T22:50:00-07:00",
    canonicalPath: "/blog/how-to-organize-tax-documents/",
    faqPage: [
      {
        question: "How should I organize tax documents for my accountant?",
        answer: "Send them grouped the way the return is built rather than as a single folder of scans: income, deductions, investments, business, payments. Name each file with the year, the form number where there is one, and the source. Most preparer follow-ups exist because a document arrived without a clear name and nobody could tell which account or which year it belonged to. Ask your accountant whether they have a preferred structure, because some firms provide one and it saves you inventing your own.",
      },
      {
        question: "Should I keep paper or digital copies of tax documents?",
        answer: "Digital, for almost everything. The IRS accepts electronic records provided they are legible, complete, and retrievable, and digital records are searchable and easy to back up. Keep the paper original only where the original itself carries legal weight, such as a deed, a title, or a notarized agreement. Scan those as well, and store the paper somewhere fireproof.",
      },
      {
        question: "What is the best way to name scanned tax documents?",
        answer: "Use tax year, then form or document type, then source: `2025_1099-INT_Chase.pdf`. Sorting alphabetically then groups all documents of the same type together without any further effort. Keep source names short and always spell them the same way. For permanent records tied to an asset, put the asset name first instead, so every document for one property sits together in date order.",
      },
      {
        question: "How do I organize tax documents if I am self-employed?",
        answer: "Use the same six folders and treat the business folder as the one that needs discipline through the year rather than at filing time. Separate business and personal accounts if you have not already, keep a mileage log as you go rather than reconstructing it in March, and file receipts monthly. The retention rules are also longer for some business records, including at least four years for employment tax records if you pay anyone.",
      },
      {
        question: "What documents should I keep forever?",
        answer: "Filed tax returns, records establishing the cost basis of anything you still own, records of non-deductible IRA contributions, entity formation documents, and vital records such as birth, marriage, and death certificates. Everything on that list either has no expiry or expires only when you sell the underlying asset, which is why it belongs in a permanent folder rather than in a tax year.",
      },
    ],
  },
  {
    id: "how-long-to-keep-tax-records",
    slug: "how-long-to-keep-tax-records",
    title: "How Long to Keep Tax Records",
    description: "Three years covers most people. That is the period the IRS generally has to assess additional tax on a return you filed, so it is how long you need the paperwork that backs up the income, deductions, and credits on it...",
    order: 32,
    image: "/assets/how-long-to-keep-tax-records.png",
    imageAlt: "How Long to Keep Tax Records",
    contentPath: "/content/how-long-to-keep-tax-records.html",
    category: "Tax",
    featured: false,
    layout: "default",
    seoTitle: "How Long to Keep Tax Records | AmitaSoft",
    seoDescription: "Three years is the usual answer, and it is wrong more often than people think. When the clock starts, the five exceptions, and the records you keep for decades.",
    publishedDate: "2026-09-01T22:42:00-07:00",
    updatedDate: "2026-09-01T22:42:00-07:00",
    canonicalPath: "/blog/how-long-to-keep-tax-records/",
    faqPage: [
      {
        question: "How long should I keep tax returns?",
        answer: "Keep copies of the filed returns indefinitely. The IRS recommends it, and they take almost no space. The three-year rule applies to the supporting documents behind the return, not to the return itself. You will need old returns to prepare future ones, to file an amended return, and to prove income for lenders and other institutions.",
      },
      {
        question: "Can I throw away tax records after 3 years?",
        answer: "Usually, but check three things first. Confirm the three years is counted from your filing date rather than the tax year end. Confirm none of the longer periods apply, particularly if you claimed a bad debt or worthless securities loss. And confirm the folder holds no records establishing the cost basis of something you still own, which is the most common reason a document should stay past three years.",
      },
      {
        question: "How long do I need to keep records for a house I sold?",
        answer: "Until the limitation period expires for the year of sale, which is generally three years after you filed the return reporting it. That means the purchase documents and improvement receipts from the entire ownership period need to survive until then. For a long-held home this can be several decades of paperwork, and it is what allows you to prove your basis and reduce the taxable gain.",
      },
      {
        question: "Do digital copies count for the IRS?",
        answer: "The IRS accepts electronic records provided they are legible, complete, and retrievable. A clear scan of a receipt is acceptable. What matters is whether you can produce the document if asked, which means a searchable and backed-up system rather than a folder of photographs on an old phone.",
      },
      {
        question: "How long should I keep tax records after someone dies?",
        answer: "Longer than usual, and this is a case to take to a professional. The estate has its own filing obligations, basis is generally re-established at death, and beneficiaries may need the decedent's records to support their own returns for years afterwards. Keep everything until the estate is fully settled and a CPA confirms what can be released.",
      },
    ],
  },
  {
    id: "tax-season-client-communication",
    slug: "tax-season-client-communication",
    title: "Tax Season Client Communication: The Six Messages a Filing Season Needs",
    description: "By late February a firm is fielding a steady stream of the same three questions. Did you get my documents. When will my return be ready. Do I need to do anything else...",
    order: 31,
    image: "/assets/tax-season-client-communication.png",
    imageAlt: "Tax Season Client Communication: The Six Messages a Filing Season Needs",
    contentPath: "/content/tax-season-client-communication.html",
    category: "Tax",
    featured: false,
    layout: "default",
    seoTitle: "Tax Season Client Communication Plan | AmitaSoft",
    seoDescription: "Most inbound client questions are a scheduling gap, not extra workload. The six messages a filing season needs, and when to write them.",
    publishedDate: "2026-09-01T22:10:00-07:00",
    updatedDate: "2026-09-01T22:10:00-07:00",
    canonicalPath: "/blog/tax-season-client-communication/",
    faqPage: [
      {
        question: "How often should a CPA firm contact clients during tax season?",
        answer: "Four to six planned messages across the season is enough for most individual engagements: the opener with the document request, one or two nudges on outstanding items, a note when work begins, and delivery. More than that starts to read as noise. Fewer than that and clients fill the silence by contacting you, which costs more time than the messages would have.",
      },
      {
        question: "What should a CPA tell a client when filing an extension?",
        answer: "Separate the two things clients conflate. Say whether payment is still due on the original deadline, and give a specific date for when the return will be filed. Add one sentence on what caused the extension and what you need from them, if anything. Having the conversation in early March rather than April keeps it a decision rather than an announcement.",
      },
      {
        question: "When should a CPA firm tell clients about a fee increase?",
        answer: "Before the engagement letter goes out, not in the invoice. A fee change delivered in December alongside the engagement terms is a normal business message. The same change discovered on an April invoice reads as a surprise, and it is the most common cause of a client leaving after an otherwise fine season.",
      },
      {
        question: "Should tax season updates go by email or client portal?",
        answer: "Whichever one you will hold to for the whole season. The channel matters less than the consistency, because the cost comes from documents and questions arriving in several places at once. Say which channel in the engagement letter, repeat it in the first message, and redirect when a client uses another.",
      },
    ],
  },
  {
    id: "cpa-firm-capacity-tax-season",
    slug: "cpa-firm-capacity-tax-season",
    title: "CPA Firm Capacity During Tax Season: Why Hiring Isn't the Fix",
    description: "Every February a managing partner does the same arithmetic. Six preparers, four hundred returns, ten weeks. It works on paper. It does not work in the office...",
    order: 30,
    image: "/assets/cpa-firm-capacity-tax-season.png",
    imageAlt: "CPA Firm Capacity During Tax Season: Why Hiring Isn't the Fix",
    contentPath: "/content/cpa-firm-capacity-tax-season.html",
    category: "Tax",
    featured: false,
    layout: "default",
    seoTitle: "CPA Firm Capacity During Tax Season | AmitaSoft",
    seoDescription: "More preparers rarely fixes a tax season. Why capacity is limited by how many returns are workable, and what to change before January.",
    publishedDate: "2026-09-01T11:26:00-07:00",
    updatedDate: "2026-09-01T11:26:00-07:00",
    canonicalPath: "/blog/cpa-firm-capacity-tax-season/",
    faqPage: [
      {
        question: "How many tax returns can one CPA prepare in a season?",
        answer: "The number varies too much to be useful as a benchmark, because it depends on return complexity, software, staffing mix, and how complete the files are when they reach the preparer. The more useful question is how many returns a preparer can finish without stopping, which is governed by document intake rather than preparation speed. A firm whose files arrive complete gets more out of the same people than a firm whose files arrive in pieces.",
      },
      {
        question: "Should a CPA firm hire seasonal staff for tax season?",
        answer: "Sometimes, but check the constraint first. If most open engagements are waiting on client documents, extra preparers will wait alongside the existing ones. If files arrive complete and preparers are the queue, seasonal help works. Hiring solves a preparation bottleneck and does nothing for an intake or review bottleneck.",
      },
      {
        question: "What is work in progress in a CPA firm?",
        answer: "Work in progress is every engagement that has been started and not delivered, including returns sitting at eighty percent waiting on a document. It matters because each open file consumes attention whether or not it is moving. Firms that cap how many engagements are open at once usually finish the season sooner than firms that open everything in January.",
      },
      {
        question: "How do you measure tax season capacity?",
        answer: "Count startable returns rather than total returns. Of everything open, how many could a preparer complete today without contacting the client. Tracked from early January, that share tells you whether your season is going to work, while there is still time to change the answer.",
      },
    ],
  },
  {
    id: "how-to-reduce-tax-season-follow-ups",
    slug: "how-to-reduce-tax-season-follow-ups",
    title: "How to Reduce Tax Season Follow-Ups",
    description: "Most follow-up emails in filing season trace back to the request that started the engagement, not to the client who ignored it. Ask for \"business expenses\" and you get a shoebox...",
    order: 29,
    image: "/assets/how-to-reduce-tax-season-follow-ups.png",
    imageAlt: "How to Reduce Tax Season Follow-Ups",
    contentPath: "/content/how-to-reduce-tax-season-follow-ups.html",
    category: "Tax",
    featured: true,
    layout: "default",
    seoTitle: "How to Reduce Tax Season Follow-Ups | AmitaSoft",
    seoDescription: "Chasing clients for documents eats the filing season. How CPA firms cut follow-up volume by fixing the request, the channel, and the outstanding list.",
    publishedDate: "2026-08-29T09:50:00-07:00",
    updatedDate: "2026-08-29T09:50:00-07:00",
    canonicalPath: "/blog/how-to-reduce-tax-season-follow-ups/",
    faqPage: [
      {
        question: "What documents does my CPA need from me?",
        answer: "For most individual returns: last year's return, W-2s and 1099s for the year being filed, mortgage interest and property tax statements, brokerage and retirement account statements, records of estimated tax payments, and records for any deduction you plan to claim. Business owners add profit and loss statements, balance sheets, payroll records, and asset purchase records. Your CPA should send a list specific to your situation rather than a generic one, because a retiree and a partnership need different things. If you received a request list that feels generic, ask for one built from your prior-year return.",
      },
      {
        question: "How many times should a CPA firm follow up before calling?",
        answer: "Two emails, then a call. A client who has ignored two written reminders will ignore a third. Firms that set this cadence in advance and assign it to one person spend less time chasing than firms that leave each preparer to decide.",
      },
      {
        question: "When should a firm send the first document request?",
        answer: "Send the initial request in December or the first week of January, before the client's documents start arriving from banks and employers. A request that lands while the client is still collecting mail gets acted on. A request that lands in late February competes with everything else on their desk.",
      },
      {
        question: "Does a tax organizer reduce follow-ups?",
        answer: "It does when someone edits it for the client. A generic organizer sent to every client produces the same ambiguity as a generic email, and clients skip whatever looks irrelevant to them. An organizer built from the prior-year return, with the irrelevant sections stripped out, gets returned more often and with fewer gaps.",
      },
    ],
  },
  {
    id: "the-end-of-static-documents-how-intelligent-workflows-are-changing-enterprise-software",
    slug: "the-end-of-static-documents-how-intelligent-workflows-are-changing-enterprise-software",
    title: "The End of Static Documents: How Intelligent Workflows Are Changing Enterprise Software",
    description: "For decades, businesses have treated documents as something to store, search, and retrieve. A contract gets uploaded. A tax document gets saved. A loan file gets archived...",
    order: 28,
    image: "/assets/the-end-of-static-documents-how-intelligent-workflows-are-changing-enterprise-software.png",
    imageAlt: "Illustration for The End of Static Documents: How Intelligent Workflows Are Changing Enterprise Software",
    contentPath: "/content/the-end-of-static-documents-how-intelligent-workflows-are-changing-enterprise-software.html",
    category: "Document Intelligence",
    featured: false,
    layout: "default",
    seoTitle: "The End of Static Documents: Intelligent Workflows | AmitaSoft",
    seoDescription: "Documents have been treated as things to store. Why intelligent workflows are changing what enterprise software does with them, and what that means in practice.",
    publishedDate: "2026-08-11T22:33:29-07:00",
    updatedDate: "2026-08-11T22:33:29-07:00",
    canonicalPath: "/blog/the-end-of-static-documents-how-intelligent-workflows-are-changing-enterprise-software/",
  },
  {
    id: "from-files-to-flow-amitasoft-is-redefining-work",
    slug: "from-files-to-flow-amitasoft-is-redefining-work",
    title: "From Files to Flow: AmitaSoft Is Redefining Work",
    description: "Every modern enterprise runs on documents. From contracts and invoices to tax records, mortgage applications, compliance reports, and customer agreements, documents are at...",
    order: 27,
    image: "/assets/from-files-to-flow-amitasoft-is-redefining-work.png",
    imageAlt: "Illustration for From Files to Flow_ AmitaSoft Is Redefining Work",
    contentPath: "/content/from-files-to-flow-amitasoft-is-redefining-work.html",
    category: "Document Intelligence",
    featured: false,
    layout: "default",
    seoTitle: "From Files to Flow: Rethinking Document Work | AmitaSoft",
    seoDescription: "Every enterprise runs on documents — contracts, invoices, tax records, loan files. Why moving from stored files to connected flow changes how work gets done.",
    publishedDate: "2026-07-23T08:42:34-07:00",
    updatedDate: "2026-07-23T08:42:34-07:00",
    canonicalPath: "/blog/from-files-to-flow-amitasoft-is-redefining-work/",
  },
  {
    id: "why-amitasoft-is-building-a-workflow-platform-around-documents",
    slug: "why-amitasoft-is-building-a-workflow-platform-around-documents",
    title: "Why AmitaSoft Is Building a Workflow Platform Around Documents",
    description: "In every business, documents are constantly moving. A sales contract is reviewed by legal, signed by the customer, sent to finance, and archived for compliance. A loan...",
    order: 26,
    image: "/assets/why-amitasoft-is-building-a-workflow-platform-around-documents.png",
    imageAlt: "Illustration for Why AmitaSoft Is Building a Workflow Platform Around Documents",
    contentPath: "/content/why-amitasoft-is-building-a-workflow-platform-around-documents.html",
    category: "Document Intelligence",
    featured: false,
    layout: "default",
    seoTitle: "Why We Built a Workflow Platform Around Documents | AmitaSoft",
    seoDescription: "Storage systems hold files. Workflow platforms move them. Why AmitaSoft built document intelligence into the workflow itself for lending, tax, and compliance.",
    publishedDate: "2026-07-18T07:20:33-07:00",
    updatedDate: "2026-07-18T07:20:33-07:00",
    canonicalPath: "/blog/why-amitasoft-is-building-a-workflow-platform-around-documents/",
  },
  {
    id: "why-financial-workflows-should-start-with-the-document",
    slug: "why-financial-workflows-should-start-with-the-document",
    title: "Why Financial Workflows Should Start With the Document",
    description: "Every financial journey starts with a document. Before a loan is approved, an invoice is paid, or a tax return is processed, someone uploads, scans, signs, or shares a...",
    order: 25,
    image: "/assets/why-financial-workflows-should-start-with-the-document.png",
    imageAlt: "Illustration for Why Financial Workflows Should Start With the Document",
    contentPath: "/content/why-financial-workflows-should-start-with-the-document.html",
    category: "Document Intelligence",
    featured: false,
    layout: "default",
    seoTitle: "Why Financial Workflows Should Start With the Document | AmitaSoft",
    seoDescription: "Financial workflows break at intake, not at the decision. Why starting with the document reduces rework, delays, and follow-ups across lending and tax.",
    publishedDate: "2026-07-09T14:38:45-07:00",
    updatedDate: "2026-07-09T14:38:45-07:00",
    canonicalPath: "/blog/why-financial-workflows-should-start-with-the-document/",
  },
  {
    id: "building-the-document-spine-for-modern-financial-life",
    slug: "building-the-document-spine-for-modern-financial-life",
    title: "Building the Document Spine for Modern Financial Life",
    description: "The financial world has become increasingly digital, but one challenge continues to affect individuals, lenders, businesses, and financial institutions alike. Critical...",
    order: 24,
    image: "/assets/building-the-document-spine-for-modern-financial-life.png",
    imageAlt: "Illustration for Building the Document Spine for Modern Financial Life",
    contentPath: "/content/building-the-document-spine-for-modern-financial-life.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-07-06T10:34:01-07:00",
    updatedDate: "2026-07-06T10:34:01-07:00",
    canonicalPath: "/blog/building-the-document-spine-for-modern-financial-life/",
    redirectTo: "/blog/why-financial-workflows-should-start-with-the-document/",
  },
  {
    id: "the-future-of-financial-paperwork-is-not-another-folder",
    slug: "the-future-of-financial-paperwork-is-not-another-folder",
    title: "The Future of Financial Paperwork Is Not Another Folder",
    description: "Financial paperwork has always been the backbone of banking, lending, insurance, and accounting. Every transaction, loan application, tax filing, compliance review, and...",
    order: 23,
    image: "/assets/the-future-of-financial-paperwork-is-not-another-folder.png",
    imageAlt: "Illustration for The Future of Financial Paperwork Is Not Another Folder",
    contentPath: "/content/the-future-of-financial-paperwork-is-not-another-folder.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-07-03T09:07:49-07:00",
    updatedDate: "2026-07-03T09:07:49-07:00",
    canonicalPath: "/blog/the-future-of-financial-paperwork-is-not-another-folder/",
    redirectTo: "/blog/why-financial-workflows-should-start-with-the-document/",
  },
  {
    id: "from-stored-files-to-reusable-financial-workflows",
    slug: "from-stored-files-to-reusable-financial-workflows",
    title: "From Stored Files to Reusable Financial Workflows",
    description: "For years, businesses have viewed documents as static records. Tax returns were stored in folders. Bank statements were uploaded to portals. Income documents were archived...",
    order: 22,
    image: "/assets/from-stored-files-to-reusable-financial-workflows.png",
    imageAlt: "Illustration for From Stored Files to Reusable Financial Workflows",
    contentPath: "/content/from-stored-files-to-reusable-financial-workflows.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-06-30T12:22:39-07:00",
    updatedDate: "2026-06-30T20:58:22-07:00",
    canonicalPath: "/blog/from-stored-files-to-reusable-financial-workflows/",
    redirectTo: "/blog/why-financial-workflows-should-start-with-the-document/",
  },
  {
    id: "how-intelligent-data-sharing-creates-business-value",
    slug: "how-intelligent-data-sharing-creates-business-value",
    title: "How Intelligent Data Sharing Creates Business Value",
    description: "In today's digital economy, data is often described as the new currency. Every customer interaction, loan application, transaction, and business process generates valuable...",
    order: 21,
    image: "/assets/how-intelligent-data-sharing-creates-business-value.png",
    imageAlt: "Illustration for How Intelligent Data Sharing Creates Business Value",
    contentPath: "/content/how-intelligent-data-sharing-creates-business-value.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-06-24T11:10:19-07:00",
    updatedDate: "2026-06-24T11:10:19-07:00",
    canonicalPath: "/blog/how-intelligent-data-sharing-creates-business-value/",
    redirectTo: "/blog/why-connected-data-ecosystems-are-the-future-of-business-operations/",
  },
  {
    id: "why-connected-data-ecosystems-are-the-future-of-business-operations",
    slug: "why-connected-data-ecosystems-are-the-future-of-business-operations",
    title: "Why Connected Data Ecosystems Are the Future of Business Operations",
    description: "In today's digital economy, businesses are generating more data than ever before. Customer interactions, financial transactions, operational workflows, compliance records...",
    order: 20,
    image: "/assets/why-connected-data-ecosystems-are-the-future-of-business-operations.png",
    imageAlt: "Illustration for Why Connected Data Ecosystems Are the Future of Business Operations",
    contentPath: "/content/why-connected-data-ecosystems-are-the-future-of-business-operations.html",
    category: "Operations",
    featured: false,
    layout: "default",
    seoTitle: "Connected Data Ecosystems in Financial Operations | AmitaSoft",
    seoDescription: "Disconnected systems duplicate work and hide status. How connected data ecosystems and intelligent sharing improve visibility across financial operations.",
    publishedDate: "2026-06-22T20:31:29-07:00",
    updatedDate: "2026-06-22T20:31:29-07:00",
    canonicalPath: "/blog/why-connected-data-ecosystems-are-the-future-of-business-operations/",
  },
  {
    id: "digital-transformation-trends-businesses-should-watch-in-2026",
    slug: "digital-transformation-trends-businesses-should-watch-in-2026",
    title: "Digital Transformation Trends Businesses Should Watch in 2026",
    description: "Digital transformation is no longer a buzzword—it has become a strategic necessity for organizations seeking growth, resilience, and long-term competitiveness. Across...",
    order: 19,
    image: "/assets/digital-transformation-trends-businesses-should-watch-in-2026.png",
    imageAlt: "Illustration for Digital Transformation Trends Businesses Should Watch in 2026",
    contentPath: "/content/digital-transformation-trends-businesses-should-watch-in-2026.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    seoTitle: "Digital Transformation Trends to Watch in 2026 | AmitaSoft",
    seoDescription: "Ten digital transformation trends shaping regulated industries in 2026 — AI in execution, cloud-first, hyperautomation, connected platforms, and digital trust.",
    publishedDate: "2026-06-15T15:14:34-07:00",
    updatedDate: "2026-06-15T15:14:34-07:00",
    canonicalPath: "/blog/digital-transformation-trends-businesses-should-watch-in-2026/",
  },
  {
    id: "top-fintech-trends-reshaping-financial-services",
    slug: "top-fintech-trends-reshaping-financial-services",
    title: "Top FinTech Trends Reshaping Financial Services",
    description: "The financial services industry is experiencing a remarkable transformation driven by innovation, automation, and evolving customer expectations. What was once a highly...",
    order: 18,
    image: "/assets/top-fintech-trends-reshaping-financial-services.png",
    imageAlt: "Illustration for Top FinTech Trends Reshaping Financial Services",
    contentPath: "/content/top-fintech-trends-reshaping-financial-services.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    seoTitle: "FinTech Trends Reshaping Financial Services | AmitaSoft",
    seoDescription: "The shifts changing how financial services are built and delivered — automation, embedded workflows, document intelligence, and rising compliance expectations.",
    publishedDate: "2026-06-10T18:32:39-07:00",
    updatedDate: "2026-06-10T18:32:39-07:00",
    canonicalPath: "/blog/top-fintech-trends-reshaping-financial-services/",
  },
  {
    id: "designing-secure-ai-systems-in-a-compliance-driven-world",
    slug: "designing-secure-ai-systems-in-a-compliance-driven-world",
    title: "Designing Secure AI Systems in a Compliance-Driven World",
    description: "Artificial intelligence is rapidly transforming industries across the United States. From financial services and mortgage lending to healthcare, legal operations, and...",
    order: 17,
    image: "/assets/designing-secure-ai-systems-in-a-compliance-driven-world.png",
    imageAlt: "Illustration for Designing Secure AI Systems in a Compliance-Driven World",
    contentPath: "/content/designing-secure-ai-systems-in-a-compliance-driven-world.html",
    category: "Security & Compliance",
    featured: false,
    layout: "default",
    seoTitle: "Document Security for Financial Firms: Designing Secure AI | AmitaSoft",
    seoDescription: "How financial firms can design AI systems that protect sensitive documents — access control, encryption, audit trails, and human review in regulated workflows.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/designing-secure-ai-systems-in-a-compliance-driven-world/",
  },
  {
    id: "you-have-every-tool-so-why-is-nothing-working",
    slug: "you-have-every-tool-so-why-is-nothing-working",
    title: "You Have Every Tool. So Why Is Nothing Working?",
    description: "More software, more subscriptions, more platforms, and somehow, less gets done than ever before. Welcome to the modern business paradox. Honestly, this is something we see...",
    order: 16,
    image: "/assets/you-have-every-tool-so-why-is-nothing-working.png",
    imageAlt: "Illustration for You Have Every Tool. So Why Is Nothing Working?",
    contentPath: "/content/you-have-every-tool-so-why-is-nothing-working.html",
    category: "Operations",
    featured: false,
    layout: "default",
    seoTitle: "Why More Software Doesn't Mean More Gets Done | AmitaSoft",
    seoDescription: "Adding tools rarely fixes throughput. Why fragmented software stacks slow document-heavy teams down, and what to change instead of buying another subscription.",
    publishedDate: "2026-06-05T20:13:02-07:00",
    updatedDate: "2026-06-05T20:13:02-07:00",
    canonicalPath: "/blog/you-have-every-tool-so-why-is-nothing-working/",
  },
  {
    id: "features-dont-fix-problemsworkflows-do",
    slug: "features-dont-fix-problemsworkflows-do",
    title: "Features Don’t Fix Problems—Workflows Do",
    description: "A lot of tools promise to reduce effort and make life easier, but they only end up making it more complicated. The real problem isn’t missing features, but it's broken...",
    order: 15,
    image: "/assets/features-dont-fix-problemsworkflows-do.png",
    imageAlt: "Illustration for Features Don’t Fix Problems—Workflows Do",
    contentPath: "/content/features-dont-fix-problemsworkflows-do.html",
    category: "Operations",
    featured: false,
    layout: "default",
    seoTitle: "Features Don't Fix Problems. Workflows Do. | AmitaSoft",
    seoDescription: "Most tools add features to problems that are structural. Why workflow design, not feature count, determines whether document-heavy work actually moves faster.",
    publishedDate: "2026-05-30T19:15:28-07:00",
    updatedDate: "2026-05-31T13:04:43-07:00",
    canonicalPath: "/blog/features-dont-fix-problemsworkflows-do/",
  },
  {
    id: "the-silent-killer-of-productivity-poor-process-handoffs",
    slug: "the-silent-killer-of-productivity-poor-process-handoffs",
    title: "The Silent Killer of Productivity? Poor Process Handoffs",
    description: "No single moment feels broken, yet somehow the process keeps slowing down. In the fast-moving world of FinTech and mortgage lending, speed is everything. Deals are...",
    order: 14,
    image: "/assets/the-silent-killer-of-productivity-poor-process-handoffs.png",
    imageAlt: "Illustration for The Silent Killer of Productivity? Poor Process Handoffs",
    contentPath: "/content/the-silent-killer-of-productivity-poor-process-handoffs.html",
    category: "Operations",
    featured: false,
    layout: "default",
    seoTitle: "Why Poor Process Handoffs Slow Lending Teams Down | AmitaSoft",
    seoDescription: "No single step feels broken, yet the file keeps stalling. How handoffs between roles create hidden delay in mortgage and document-driven workflows.",
    publishedDate: "2026-05-26T22:54:06-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/the-silent-killer-of-productivity-poor-process-handoffs/",
  },
  {
    id: "the-problem-with-modern-work-isnt-speed-its-recovery-time",
    slug: "the-problem-with-modern-work-isnt-speed-its-recovery-time",
    title: "The Problem with Modern Work Isn’t Speed. It’s Recovery Time",
    description: "Modern organizations are obsessed with speed. Faster execution. Faster approvals. Faster delivery. Faster communication. Every tool promises acceleration. But speed isn’t the...",
    order: 13,
    image: "/assets/the-problem-with-modern-work-isnt-speed-its-recovery-time.png",
    imageAlt: "Illustration for The Problem with Modern Work Isn’t Speed. It’s Recovery Time",
    contentPath: "/content/the-problem-with-modern-work-isnt-speed-its-recovery-time.html",
    category: "Operations",
    featured: false,
    layout: "default",
    seoTitle: "The Real Bottleneck Isn't Speed — It's Recovery Time | AmitaSoft",
    seoDescription: "Teams optimise for faster execution and still fall behind. Why recovery time after exceptions, not raw speed, determines how quickly work actually completes.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-06-10T18:32:39-07:00",
    canonicalPath: "/blog/the-problem-with-modern-work-isnt-speed-its-recovery-time/",
  },
  {
    id: "operational-clarity-is-the-new-competitive-advantage",
    slug: "operational-clarity-is-the-new-competitive-advantage",
    title: "Operational Clarity Is the New Competitive Advantage",
    description: "For years, businesses have been told that speed is everything. Move faster. Respond faster. Automate faster. Close faster. Scale faster. Speed has become the language of...",
    order: 12,
    image: "/assets/operational-clarity-is-the-new-competitive-advantage.png",
    imageAlt: "Illustration for Operational Clarity Is the New Competitive Advantage",
    contentPath: "/content/operational-clarity-is-the-new-competitive-advantage.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-31T13:04:43-07:00",
    canonicalPath: "/blog/operational-clarity-is-the-new-competitive-advantage/",
    redirectTo: "/blog/why-operational-clarity-must-come-from-system-design/",
  },
  {
    id: "why-operational-clarity-must-come-from-system-design",
    slug: "why-operational-clarity-must-come-from-system-design",
    title: "Why Operational Clarity Must Come From System Design",
    description: "In regulated, document-heavy industries, operational failures are almost always blamed on people or gaps in process. When delays occur or errors surface, the response is...",
    order: 11,
    image: "/assets/why-operational-clarity-must-come-from-system-design.png",
    imageAlt: "Illustration for Why Operational Clarity Must Come From System Design",
    contentPath: "/content/why-operational-clarity-must-come-from-system-design.html",
    category: "Operations",
    featured: false,
    layout: "default",
    seoTitle: "Operational Clarity Comes From System Design, Not Effort | AmitaSoft",
    seoDescription: "In regulated, document-heavy work, clarity is a property of the system, not the team. How workflow design produces visibility that process discipline cannot.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-31T13:04:43-07:00",
    canonicalPath: "/blog/why-operational-clarity-must-come-from-system-design/",
  },
  {
    id: "the-three-pillars-of-enterprise-digital-transformation",
    slug: "the-three-pillars-of-enterprise-digital-transformation",
    title: "The Three Pillars of Enterprise Digital Transformation",
    description: "From Vision to Execution Why Enterprise Digital Transformation Is No Longer Optional Enterprise digital transformation is no longer optional. Organizations across industries...",
    order: 10,
    image: "/assets/the-three-pillars-of-enterprise-digital-transformation.png",
    imageAlt: "Illustration for The Three Pillars of Enterprise Digital Transformation",
    contentPath: "/content/the-three-pillars-of-enterprise-digital-transformation.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    seoTitle: "The Three Pillars of Enterprise Digital Transformation | AmitaSoft",
    seoDescription: "Strategy, systems, and adoption. The three conditions that determine whether enterprise digital transformation delivers results or stalls after implementation.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-06-10T18:32:39-07:00",
    canonicalPath: "/blog/the-three-pillars-of-enterprise-digital-transformation/",
  },
  {
    id: "when-audit-trails-are-optional-risk-is-guaranteed",
    slug: "when-audit-trails-are-optional-risk-is-guaranteed",
    title: "When Audit Trails Are Optional, Risk Is Guaranteed",
    description: "Audit Readiness Must Be Enforced — Not Reconstructed In regulated, document-driven industries, audit readiness is often treated as something to deal with later. As long as...",
    order: 9,
    image: "/assets/when-audit-trails-are-optional-risk-is-guaranteed.png",
    imageAlt: "Illustration for When Audit Trails Are Optional, Risk Is Guaranteed",
    contentPath: "/content/when-audit-trails-are-optional-risk-is-guaranteed.html",
    category: "Security & Compliance",
    featured: false,
    layout: "default",
    seoTitle: "Audit Trails in Regulated Workflows: Why Optional Fails | AmitaSoft",
    seoDescription: "Audit readiness cannot be reconstructed after the fact. How built-in audit trails, accountability, and human review make decisions defensible in regulated work.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/when-audit-trails-are-optional-risk-is-guaranteed/",
  },
  {
    id: "why-accountability-must-be-built-into-software-not-written-into-policy",
    slug: "why-accountability-must-be-built-into-software-not-written-into-policy",
    title: "Why Accountability Must Be Built Into Software, Not Written Into Policy",
    description: "Accountability in Regulated Industries: The Illusion of Control In regulated and document-intensive industries, accountability is often treated as a paperwork exercise...",
    order: 8,
    image: "/assets/why-accountability-must-be-built-into-software-not-written-into-policy.png",
    imageAlt: "Illustration for Why Accountability Must Be Built Into Software, Not Written Into Policy",
    contentPath: "/content/why-accountability-must-be-built-into-software-not-written-into-policy.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/why-accountability-must-be-built-into-software-not-written-into-policy/",
    redirectTo: "/blog/when-audit-trails-are-optional-risk-is-guaranteed/",
  },
  {
    id: "the-difference-between-tracking-work-and-defending-decisions",
    slug: "the-difference-between-tracking-work-and-defending-decisions",
    title: "The Difference Between Tracking Work and Defending Decisions",
    description: "Visibility Shows Activity. Defensibility Explains Accountability. In regulated, document-driven industries, modern platforms are built to provide visibility. Dashboards show...",
    order: 7,
    image: "/assets/the-difference-between-tracking-work-and-defending-decisions.png",
    imageAlt: "Illustration for The Difference Between Tracking Work and Defending Decisions",
    contentPath: "/content/the-difference-between-tracking-work-and-defending-decisions.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-31T13:04:43-07:00",
    canonicalPath: "/blog/the-difference-between-tracking-work-and-defending-decisions/",
    redirectTo: "/blog/when-audit-trails-are-optional-risk-is-guaranteed/",
  },
  {
    id: "human-in-the-loop-for-decision-defense-where-to-draw-the-line-in-regulated-systems",
    slug: "human-in-the-loop-for-decision-defense-where-to-draw-the-line-in-regulated-systems",
    title: "Human-in-the-Loop for Decision Defense: Where To Draw the Line in Regulated Systems",
    description: "Automation in Regulated Industries Is Advancing — But Accountability Cannot Recede Automation in regulated industries is accelerating at an unprecedented pace. In mortgage...",
    order: 6,
    image: "/assets/human-in-the-loop-for-decision-defense-where-to-draw-the-line-in-regulated-systems.png",
    imageAlt: "Illustration for Human-in-the-Loop for Decision Defense: Where To Draw the Line in Regulated Systems",
    contentPath: "/content/human-in-the-loop-for-decision-defense-where-to-draw-the-line-in-regulated-systems.html",
    category: "Digital Transformation",
    featured: false,
    layout: "default",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/human-in-the-loop-for-decision-defense-where-to-draw-the-line-in-regulated-systems/",
    redirectTo: "/blog/when-audit-trails-are-optional-risk-is-guaranteed/",
  },
  {
    id: "ai-cloud-security-the-trinity-shaping-digital-trust",
    slug: "ai-cloud-security-the-trinity-shaping-digital-trust",
    title: "AI + Cloud + Security: The Trinity Shaping Digital Trust",
    description: "Trust has become the quiet currency of the digital world. Every time someone logs into a platform, shares sensitive data, approves a transaction, or relies on an automated...",
    order: 5,
    image: "/assets/ai-cloud-security-the-trinity-shaping-digital-trust.png",
    imageAlt: "Illustration for AI + Cloud + Security: The Trinity Shaping Digital Trust",
    contentPath: "/content/ai-cloud-security-the-trinity-shaping-digital-trust.html",
    category: "Security & Compliance",
    featured: false,
    layout: "default",
    seoTitle: "AI, Cloud and Security: The Foundations of Digital Trust | AmitaSoft",
    seoDescription: "Digital trust rests on three dependencies — intelligent systems, cloud infrastructure, and security controls. How they combine in regulated document workflows.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-06-10T18:32:39-07:00",
    canonicalPath: "/blog/ai-cloud-security-the-trinity-shaping-digital-trust/",
  },
  {
    id: "how-ai-is-quietly-powering-the-worlds-most-reliable-digital-systems",
    slug: "how-ai-is-quietly-powering-the-worlds-most-reliable-digital-systems",
    title: "How AI Is Quietly Powering the World’s Most Reliable Digital Systems",
    description: "Most people don’t think about reliability until it’s gone. When a payment fails at checkout. When a platform freezes during a critical moment. When data doesn’t sync...",
    order: 4,
    image: "/assets/how-ai-is-quietly-powering-the-worlds-most-reliable-digital-systems.png",
    imageAlt: "Illustration for How AI Is Quietly Powering the World’s Most Reliable Digital Systems",
    contentPath: "/content/how-ai-is-quietly-powering-the-worlds-most-reliable-digital-systems.html",
    category: "AI & Automation",
    featured: false,
    layout: "default",
    seoTitle: "How AI Quietly Powers Reliable Digital Systems | AmitaSoft",
    seoDescription: "Reliability is invisible until it fails. How AI supports the systems people depend on daily, and where automated decisions still need human oversight.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-31T13:04:43-07:00",
    canonicalPath: "/blog/how-ai-is-quietly-powering-the-worlds-most-reliable-digital-systems/",
  },
  {
    id: "reimagining-fintech-platforms-with-intelligent-risk-detection",
    slug: "reimagining-fintech-platforms-with-intelligent-risk-detection",
    title: "Reimagining Fintech Platforms with Intelligent Risk Detection",
    description: "Smarter Risk. Faster Decisions. Stronger Trust. Fintech innovation has transformed how financial services are built and delivered but it has also redefined risk. Real-time...",
    order: 3,
    image: "/assets/reimagining-fintech-platforms-with-intelligent-risk-detection.png",
    imageAlt: "Illustration for Reimagining Fintech Platforms with Intelligent Risk Detection",
    contentPath: "/content/reimagining-fintech-platforms-with-intelligent-risk-detection.html",
    category: "AI & Automation",
    featured: false,
    layout: "default",
    seoTitle: "AI in Mortgage Lending: Intelligent Risk Detection | AmitaSoft",
    seoDescription: "How AI is applied in mortgage lending and fintech risk detection — document review, exception routing, and where human judgement still has to sit.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/reimagining-fintech-platforms-with-intelligent-risk-detection/",
  },
  {
    id: "from-document-chaos-to-decision-ready-workflows-how-amitasoft-connects-safevault-and-cliqloan",
    slug: "from-document-chaos-to-decision-ready-workflows-how-amitasoft-connects-safevault-and-cliqloan",
    title: "From Document Chaos to Decision-Ready Workflows: How AmitaSoft Connects SafeVault and CliQloan",
    description: "In many financial and document-heavy workflows, the real delay does not begin at the decision stage. It begins much earlier. It starts when documents are scattered across...",
    order: 2,
    image: "/assets/from-document-chaos-to-decision-ready-workflows-how-amitasoft-connects-safevault-and-cliqloan.png",
    imageAlt: "Illustration for From Document Chaos to Decision-Ready Workflows: How AmitaSoft Connects SafeVault and CliQloan",
    contentPath: "/content/from-document-chaos-to-decision-ready-workflows-how-amitasoft-connects-safevault-and-cliqloan.html",
    category: "Document Intelligence",
    featured: false,
    layout: "default",
    seoTitle: "How SafeVault and CliQloan Connect Document Workflows | AmitaSoft",
    seoDescription: "Delays start at document collection, not at the decision. How SafeVault and Cliqloan connect intake, verification, and loan file workflow in one ecosystem.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/from-document-chaos-to-decision-ready-workflows-how-amitasoft-connects-safevault-and-cliqloan/",
  },
  {
    id: "how-better-document-verification-can-speed-up-loan-approval",
    slug: "how-better-document-verification-can-speed-up-loan-approval",
    title: "How Better Document Verification Can Speed Up Loan Approval",
    description: "Loan approvals do not usually slow down at the final decision. They slow down much earlier, when document collection is fragmented, verification takes too long, conditions...",
    order: 1,
    image: "/assets/how-better-document-verification-can-speed-up-loan-approval.png",
    imageAlt: "Illustration for How Better Document Verification Can Speed Up Loan Approval",
    contentPath: "/content/how-better-document-verification-can-speed-up-loan-approval.html",
    category: "Lending",
    featured: false,
    layout: "default",
    seoTitle: "Why Mortgage Loans Get Delayed — and How to Fix It | AmitaSoft",
    seoDescription: "Mortgage delays rarely start at underwriting. Where loan files actually stall, which documents cause rework, and how better verification speeds approval.",
    publishedDate: "2026-05-21T13:47:55-07:00",
    updatedDate: "2026-05-27T06:44:05-07:00",
    canonicalPath: "/blog/how-better-document-verification-can-speed-up-loan-approval/",
  },
  {
    id: "blog-v2-template-verification",
    slug: "blog-v2-template-verification",
    title: "Blog V2 Template Verification",
    description: "A noindex publishing-workflow check that a new AmitaSoft post inherits the shared Blog V2 template without page-specific CSS.",
    order: 0,
    image: "/assets/blog-v2-template-verification.png",
    imageAlt: "AmitaSoft Blog V2 template verification",
    contentPath: "/content/blog-v2-template-verification.html",
    category: "Document Intelligence",
    featured: false,
    layout: "default",
    qaFixture: true,
    noIndex: true,
    seoTitle: "Blog V2 Template Verification | AmitaSoft",
    seoDescription: "Publishing-workflow check: a new AmitaSoft blog post inherits Blog V2 without page-specific CSS. Noindex.",
    publishedDate: "2026-08-31T11:30:00-07:00",
    updatedDate: "2026-08-31T11:30:00-07:00",
    canonicalPath: "/blog/blog-v2-template-verification/",
  },
];

const includeQaFixtures = () =>
  typeof process !== "undefined" &&
  process.env.REACT_APP_INCLUDE_QA_FIXTURES === "true";

const isAvailableInThisBuild = (post) => !post.qaFixture || includeQaFixtures();

/** @typedef {{
 *  id: string, slug: string, title: string, description: string, order: number,
 *  image: string, imageAlt: string, contentPath: string, category?: string,
 *  publishedDate?: string, updatedDate?: string, layout?: "left"|"right"|"default",
 *  featured?: boolean, seoTitle?: string, seoDescription?: string, canonicalPath?: string,
 *  publishedDate?: string, updatedDate?: string, redirectTo?: string,
 *  legacyId?: number, faqPage?: { question: string, answer: string }[]
 * }} BlogPost */

export const getLiveBlogPosts = () => blogPosts.filter((post) => !post.redirectTo && isAvailableInThisBuild(post));

export const getSortedBlogPosts = () => getLiveBlogPosts().filter((post) => !post.noIndex).sort((a, b) => b.order - a.order);

export const getBlogPostBySlug = (slug) => blogPosts.find((post) => post.slug === slug && isAvailableInThisBuild(post));

export const getBlogPostByLegacyId = (id) => blogPosts.find((post) => post.legacyId === id && isAvailableInThisBuild(post));

export const LEGACY_ID_TO_SLUG = blogPosts.reduce((acc, post) => {
  if (post.legacyId != null) acc[post.legacyId] = post.slug;
  return acc;
}, {});
